import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Link } from 'react-router-dom'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
}

export function CookieConsentBanner() {
  const { t } = useTranslation()
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences)

  const { data: consentStatus } = useQuery({
    queryKey: ['cookie-consent-status'],
    queryFn: async () => {
      try {
        const response = await apiRequest<{
          platform_terms: { accepted: boolean }
          business_terms: { accepted: boolean }
        }>('/v1/terms/status')
        return response
      } catch {
        return null
      }
    },
    staleTime: Infinity,
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent')
    if (!consent && consentStatus) {
      setShowBanner(true)
    }
  }, [consentStatus])

  const saveConsent = useMutation({
    mutationFn: async (_prefs: CookiePreferences) => {
      return apiRequest<{ message: string }>('/v1/terms/accept-platform', {
        method: 'POST',
      })
    },
    onSuccess: () => {
      localStorage.setItem('cookie-consent', JSON.stringify(preferences))
      setShowBanner(false)
      setShowPreferences(false)
    },
  })

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    saveConsent.mutate(allAccepted)
  }

  const handleAcceptNecessary = () => {
    setPreferences(defaultPreferences)
    saveConsent.mutate(defaultPreferences)
  }

  const handleSavePreferences = () => {
    saveConsent.mutate(preferences)
  }

  if (!showBanner) return null

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t shadow-lg">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium">
                {t('cookies.banner.title', 'We value your privacy')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('cookies.banner.description', 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}
              </p>
              <div className="text-xs text-muted-foreground">
                <Link to="/legal/cookie_policy" className="underline hover:text-foreground">
                  {t('cookies.banner.learnMore', 'Learn more about our Cookie Policy')}
                </Link>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreferences(true)}
              >
                {t('cookies.banner.preferences', 'Preferences')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
              >
                {t('cookies.banner.necessaryOnly', 'Necessary Only')}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
              >
                {t('cookies.banner.acceptAll', 'Accept All')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('cookies.preferences.title', 'Cookie Preferences')}
            </DialogTitle>
            <DialogDescription>
              {t('cookies.preferences.description', 'Customize your cookie preferences below.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>
                  {t('cookies.preferences.necessary.title', 'Necessary Cookies')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('cookies.preferences.necessary.description', 'Required for the website to function properly. Cannot be disabled.')}
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>
                  {t('cookies.preferences.analytics.title', 'Analytics Cookies')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('cookies.preferences.analytics.description', 'Help us understand how visitors interact with our website.')}
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, analytics: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>
                  {t('cookies.preferences.marketing.title', 'Marketing Cookies')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('cookies.preferences.marketing.description', 'Used to deliver personalized advertisements and measure their effectiveness.')}
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, marketing: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPreferences(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSavePreferences}
              disabled={saveConsent.isPending}
            >
              {saveConsent.isPending
                ? t('common.saving', 'Saving...')
                : t('cookies.preferences.save', 'Save Preferences')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}